// What the browser is running, and whether the server agrees (#54).
//
// The version shown is the bundle's own, compiled in at build time, because
// that describes the code executing in front of the person reading it. The
// server's is fetched and compared: index.html is uncached and assets are
// hashed (ZENITH.md §18), so a reload always lands on the current bundle, but
// a tab left open does not, and that tab must not claim to be current.
import { useEffect, type ReactNode } from "react";
import type { components } from "../api/schema";
import { api, unattended } from "../api/client";
import { UNATTENDED, useQuery } from "../api/hooks";
import { FiguresAge } from "./freshness";

export const BUNDLE = { version: __ZENITH_VERSION__, commit: __ZENITH_COMMIT__ };

/** How long a tab that stays visible waits between looks at /version. Not a
 * §29 cadence: it reads Zenith's own version, never a cell, and the look on
 * every return to the tab is what catches a deploy — this only covers a tab
 * nobody leaves. Long on purpose (#186). */
export const RECHECK_INTERVAL_MS = 15 * 60 * 1000;

/** What the server runs, read once for the whole shell, and whether this
 * bundle is behind it. One reader, because on a phone the answer is shown in
 * two places — the notice in the header, the build in the account menu (#1132)
 * — and two readers would be two looks at /version on every return to the tab. */
export type VersionCheck = { server?: components["schemas"]["Version"]; stale: boolean };

export function useVersionCheck(): VersionCheck {
  // Read at mount, again whenever the tab is shown, and on a timer that is
  // re-armed only by an answer: while the API answers nothing — a restart —
  // nothing retries against it, and the next look is the person's return to
  // the tab. A deploy that drops the session needs no listener here: the
  // shell unmounts to the sign-in page and this mounts again, and reads, when
  // they are back in.
  // The timer's look is marked unattended and is not use (ADR-0192 decision
  // 3b): a tab nobody leaves does not keep its session alive by checking the
  // version. The look on the person's return to the tab is use.
  const { status, data: server, reload } = useQuery((how) => api.GET("/version", { params: { header: unattended(how) } }), []);
  useEffect(() => {
    if (status !== "ready") return;
    const id = setTimeout(() => reload(UNATTENDED), RECHECK_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [status, server, reload]);
  useEffect(() => {
    const onShow = () => { if (!document.hidden) reload(); };
    document.addEventListener("visibilitychange", onShow);
    return () => document.removeEventListener("visibilitychange", onShow);
  }, [reload]);
  const stale = server !== undefined && server.version !== BUNDLE.version;
  return { server, stale };
}

/** The build this bundle was made from, the release it was promoted as where
 * there is one, and under them how old the figures on screen are (#1315). At
 * the foot of the wide navigation, and in the account menu on a phone (#1132). */
export function BuildLabel({ server, children }: { server?: VersionCheck["server"]; children?: ReactNode }) {
  return (
    <div className="build">
      {/* Restates ADR-0185 decision 4, option 3a — the word `build` says this value names
        * the bytes this bundle was made from, and not the release those bytes were promoted
        * as. Promotion to production is a retag of the release candidate's image, never a
        * rebuild, so production serves an image stamped `0.1.0-rc.N` while the release is
        * `0.1.0`; unlabelled, the footer reads as a claim that production is running a
        * release candidate. Reporting the release beside the build is #1060's and is not
        * here, and outside production there is no release to report at all. */}
      build <span className="build-version" title={`commit ${BUNDLE.commit}`}>{BUNDLE.version}</span>
      {/* Restates ADR-0185 decision 2, option 1b — the release the server was promoted
        * as, shown BESIDE the build and never in place of it, because both are true of
        * the same image. It is the server's answer and not the bundle's: a release names
        * a promotion of the deployed image, which the bundle knows nothing about, and
        * __ZENITH_VERSION__ deliberately keeps reporting the bytes so §18's out-of-date
        * comparison above still compares code identity (that is #1060's Out).
        *
        * Absent is the normal case and renders nothing at all: outside production no
        * promotion has named this build, so there is no release to show. Nothing here
        * falls back to the build — a build that was never promoted has no release name,
        * and showing the build under that word would assert the promotion happened. */}
      {server?.release !== undefined && (
        <span className="build-release"> · release <span className="build-release-version">{server.release}</span></span>
      )}
      {/* How old the figures on screen are, under the build (#1315): the one
        * place that says it, for every page. `shell/freshness.tsx` carries the
        * decisions and the markers. Rendered here rather than beside this label
        * in each of its two places, so the phone's account menu carries it too. */}
      <FiguresAge />
      {children}
    </div>
  );
}

/** The out-of-date notice, which §18 keeps visible: on a phone it stays in the
 * header on every page while the build label moves into the account menu (#1132). */
export function StaleNotice({ server }: { server?: VersionCheck["server"] }) {
  return (
    <span className="build-stale">
      This page is out of date. The server runs build {server?.version}.{" "}
      <button type="button" className="btn btn-quiet" onClick={() => window.location.reload()}>Reload</button>
    </span>
  );
}

/** The foot of the wide navigation: the build, and the notice under it when
 * this bundle is behind the server. */
export default function Version({ check }: { check: VersionCheck }) {
  return (
    <BuildLabel server={check.server}>
      {check.stale && <StaleNotice server={check.server} />}
    </BuildLabel>
  );
}
